import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Search, Loader2, AlertTriangle, RefreshCcw, Inbox } from "lucide-react";

import OperationsShell from "@/components/operations/OperationsShell";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import { api, formatApiError } from "@/lib/api";

/**
 * OPS-CUST-001 · Operations → Customers workspace (list view).
 *
 * Replaces the "Module under construction" placeholder with a
 * canonical, read-only customer roster:
 *
 *   * Search across name / email / phone / postcode / address /
 *     project reference (handled server-side by the shared read
 *     endpoint that also powers the future Customer → My System view).
 *   * Active / Archived / All filter driven by `account_status`.
 *   * URL state (`?q=…&filter=…&selected=…`) survives refresh and
 *     Back navigation.
 *   * Selecting a row navigates to the detail route
 *     (`/operations/customers/:id`), carrying `?q` + `?filter` in the
 *     `return` param so closing detail restores the list state.
 *
 * NO writes. Zero customer / account mutations from this route.
 */
const FILTERS = [
    { key: "active",   label: "Active" },
    { key: "archived", label: "Archived" },
    { key: "all",      label: "All" },
];

function encodeReturn(params) {
    const usp = new URLSearchParams();
    if (params.q) usp.set("q", params.q);
    if (params.filter && params.filter !== "active") usp.set("filter", params.filter);
    const s = usp.toString();
    return s ? `?${s}` : "";
}

function accountBadge(row) {
    const status = (row.account_status || "active").toLowerCase();
    if (status === "disabled") return { label: "Archived", cls: "bg-[#F5F5F7] text-[#4B5565]" };
    if (status === "suspended") return { label: "Suspended", cls: "bg-[#FEF3C7] text-[#92400E]" };
    return { label: "Active", cls: "bg-[#EAF6E1] text-[#3E9E2E]" };
}

export default function OperationsCustomers() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    // OPS-CUST-001A1 · Conventional controlled-input pattern.
    // The URL is the source of truth for `q`/`filter`; a separate
    // `draft` state owns what the user is currently typing. The two
    // are only synchronised when a REAL navigation changes the URL
    // query — never on every render, and never mid-typing.
    const urlQ = searchParams.get("q") || "";
    const filter = searchParams.get("filter") || "active";

    const [draft, setDraft] = useState(urlQ);
    const lastSyncedUrlQRef = useRef(urlQ);
    useEffect(() => {
        if (urlQ !== lastSyncedUrlQRef.current) {
            lastSyncedUrlQRef.current = urlQ;
            setDraft(urlQ);
        }
    }, [urlQ]);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const resp = await api.opsListCustomers({ q: urlQ, filter });
            setRows(resp.customers || []);
        } catch (e) {
            const msg = formatApiError(e?.response?.data?.detail) || "Failed to load customers";
            setErr(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, [urlQ, filter]);

    useEffect(() => { load(); }, [load]);

    const setUrl = useCallback((patch) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            Object.entries(patch).forEach(([k, v]) => {
                if (v === "" || v == null || (k === "filter" && v === "active")) next.delete(k);
                else next.set(k, v);
            });
            return next;
        }, { replace: false });
    }, [setSearchParams]);

    const onSubmitSearch = (e) => {
        e.preventDefault();
        // Trim on submit only — do NOT mutate `draft` while the user
        // is still typing so the visible textbox is authoritative.
        setUrl({ q: draft.trim() });
    };

    const openCustomer = (row) => {
        navigate(`/operations/customers/${row.id}${encodeReturn({ q: urlQ, filter })}`);
    };

    const empty = !loading && !err && rows.length === 0;
    const noResults = !loading && !err && rows.length === 0 && (urlQ || filter !== "active");

    return (
        <OperationsShell>
            <PageContainer width="full" testid="ops-customers-page">
                <PageHeader
                    eyebrow="OPERATIONS"
                    title="Customers"
                    subtitle="Search homeowners and open their projects or Security Administration record."
                    icon={Users}
                    backFallback="/operations"
                    testid="ops-customers"
                />

                {/* Toolbar */}
                <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3">
                    <form onSubmit={onSubmitSearch} className="flex-1 relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5565]" />
                        <input
                            data-testid="ops-customers-search"
                            className="w-full h-10 pl-9 pr-3 rounded-md border border-[#E5E7EB] bg-[#FAFAFA] focus:bg-white focus:border-[#131B2F] outline-none text-sm"
                            placeholder="Search name, email, phone, postcode or project reference…"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                        />
                    </form>
                    <div className="flex items-center gap-2">
                        {FILTERS.map((f) => (
                            <button
                                key={f.key}
                                data-testid={`ops-customers-filter-${f.key}`}
                                onClick={() => setUrl({ filter: f.key })}
                                className={
                                    "h-8 px-3 rounded-full text-xs font-medium border transition-colors " +
                                    (filter === f.key
                                        ? "bg-[#131B2F] text-white border-[#131B2F]"
                                        : "bg-white text-[#131B2F] border-[#E5E7EB] hover:bg-[#F5F5F7]")
                                }
                                aria-pressed={filter === f.key}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Body */}
                <div className="mt-4 rounded-xl border border-[#E5E7EB] bg-white overflow-hidden" data-testid="ops-customers-list">
                    {loading && (
                        <div className="p-10 flex items-center justify-center text-sm text-[#4B5565]" data-testid="ops-customers-loading">
                            <Loader2 className="animate-spin mr-2" size={16} /> Loading customers…
                        </div>
                    )}
                    {!loading && err && (
                        <div className="p-10 text-center" data-testid="ops-customers-error">
                            <AlertTriangle size={22} className="mx-auto text-[#B91C1C]" />
                            <div className="mt-3 text-sm text-[#131B2F]">{err}</div>
                            <button
                                onClick={load}
                                className="mt-4 inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                                data-testid="ops-customers-retry"
                            >
                                <RefreshCcw size={14} /> Try again
                            </button>
                        </div>
                    )}
                    {!loading && !err && empty && !noResults && (
                        <div className="p-12 text-center" data-testid="ops-customers-empty">
                            <Inbox size={22} className="mx-auto text-[#9CA3AF]" />
                            <div className="mt-3 text-sm text-[#131B2F]">No customers yet.</div>
                            <div className="mt-1 text-xs text-[#4B5565]">Customers appear here once a project is created for them.</div>
                        </div>
                    )}
                    {!loading && !err && noResults && (
                        <div className="p-12 text-center" data-testid="ops-customers-no-results">
                            <Search size={22} className="mx-auto text-[#9CA3AF]" />
                            <div className="mt-3 text-sm text-[#131B2F]">No matches for the current search or filter.</div>
                            <button
                                onClick={() => { setDraft(""); setUrl({ q: "", filter: "active" }); }}
                                className="mt-4 inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                                data-testid="ops-customers-clear"
                            >
                                Clear filters
                            </button>
                        </div>
                    )}
                    {!loading && !err && rows.length > 0 && (
                        <ul className="divide-y divide-[#EDEFF3]">
                            {rows.map((row) => {
                                const badge = accountBadge(row);
                                return (
                                    <li
                                        key={row.id}
                                        data-testid={`ops-customer-row-${row.id}`}
                                        className="p-4 md:p-5 hover:bg-[#FAFAFA] cursor-pointer transition-colors"
                                        onClick={() => openCustomer(row)}
                                    >
                                        <div className="flex flex-col md:flex-row md:items-center md:gap-6">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <div className="text-sm md:text-base font-medium text-[#131B2F] truncate">
                                                        <span data-testid={`cust-name-${row.id}`}>{row.name || row.email || "Unnamed customer"}</span>
                                                    </div>
                                                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                                                </div>
                                                <div className="mt-1 text-xs text-[#4B5565] truncate">
                                                    <span data-testid={`cust-email-${row.id}`}>{row.email || "—"}</span>{row.mobile ? ` · ${row.mobile}` : ""}
                                                </div>
                                            </div>
                                            <div className="mt-3 md:mt-0 flex items-center gap-4 md:gap-6 text-xs text-[#131B2F]">
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] uppercase tracking-wide text-[#4B5565]">Projects</span>
                                                    <span className="font-medium" data-testid={`ops-customer-count-${row.id}`}>
                                                        {row.linked_project_count} <span className="text-[#4B5565] font-normal">({row.active_project_count} active)</span>
                                                    </span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] uppercase tracking-wide text-[#4B5565]">Latest stage</span>
                                                    <span className="font-medium">{row.current_stage || "—"}</span>
                                                </div>
                                                <Link
                                                    to={`/operations/customers/${row.id}${encodeReturn({ q: urlQ, filter })}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="hidden md:inline-block text-xs text-[#3E9E2E] hover:underline"
                                                    data-testid={`ops-customer-open-${row.id}`}
                                                >
                                                    Open →
                                                </Link>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </PageContainer>
        </OperationsShell>
    );
}
